import { useState, useRef, useCallback, useEffect } from "react";
import "../components/ToastNotification.css";

let _nextId = 1;

export default function useNotifications() {
  const [toasts, setToasts] = useState([]);
  const initialPermission = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
  const permissionRef = useRef(initialPermission);
  const [permissionState, setPermissionState] = useState(initialPermission);

  const requestPermission = useCallback(async () => {
    if (permissionRef.current === "unsupported" || permissionRef.current === "granted") return;
    try {
      const result = await Notification.requestPermission();
      permissionRef.current = result;
      setPermissionState(result);
    } catch {
      permissionRef.current = "denied";
      setPermissionState("denied");
    }
  }, []);

  // Request permission proactively on mount so the browser prompt appears
  // before the user switches tabs (lazy request would never fire otherwise).
  useEffect(() => {
    if (permissionRef.current === "default") {
      requestPermission();
    }
  }, [requestPermission]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(async ({ type = "info", title, body, duration = 5000, browserOnly = false }) => {
    if (!browserOnly) {
      const id = _nextId++;
      setToasts((prev) => [...prev, { id, type, title, body, duration }]);
    }

    if (document.visibilityState !== "hidden") return;
    if (permissionRef.current !== "granted") return;
    try {
      const n = new Notification(title, { body, tag: `forge-${type}` });
      setTimeout(() => n.close(), 8000);
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* NotAllowedError in some focus/DND modes */ }
  }, []);

  // eslint-disable-next-line react/display-name
  const ToastContainer = useCallback(() => {
    if (toasts.length === 0) return null;
    return (
      <div className="toast-container" role="region" aria-live="polite" aria-label="Notifications">
        {toasts.map((t) => <Toast key={t.id} toast={t} onDismiss={dismiss} />)}
      </div>
    );
  }, [toasts, dismiss]);

  return { notify, ToastContainer, permissionState, requestPermission };
}

function Toast({ toast, onDismiss }) {
  const { id, type, title, body, duration } = toast;

  useEffect(() => {
    if (duration === Infinity) return;
    const t = setTimeout(() => onDismiss(id), duration);
    return () => clearTimeout(t);
  }, [id, duration, onDismiss]);

  const ICONS = { success: "✓", error: "✕", warning: "⚠", info: "ℹ" };

  return (
    <div className={`toast toast--${type}`} role="alert">
      <div className="toast-icon">{ICONS[type]}</div>
      <div className="toast-content">
        <p className="toast-title">{title}</p>
        {body && <p className="toast-body">{body}</p>}
      </div>
      <button className="toast-close" onClick={() => onDismiss(id)} aria-label="Dismiss notification">×</button>
    </div>
  );
}
